import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import Icon from '../components/atoms/Icon';
import { sendOtp, verifyOtp, signupCandidate, checkUserExists } from '../services/api';
import { googleAuth } from '../services/googleAuth';
import { initiateLinkedInAuth } from '../services/linkedinAuth';
import SessionManager from '../utils/sessionManager';
import { GOOGLE_CLIENT_ID } from '../config';

const SignupPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '',
    phoneCountryCode: '+1', phoneNumber: '',
    password: '', confirmPassword: '', acceptTerms: false
  });
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [otp, setOtp] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setFeedback({ type: '', message: '' });
      const response = await googleAuth(credentialResponse.credential, 'candidate');
      SessionManager.setSession('candidate', response);
      setFeedback({ type: 'success', message: 'Account created! Redirecting...' });
      setTimeout(() => navigate('/onboarding'), 1500);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Google signup failed.' });
    }
  };

  const handleGoogleError = () => {
    setFeedback({ type: 'error', message: 'Google signup failed. Please try again.' });
  };

  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) { setCanResendOtp(true); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const formatTimer = (s) => {
    return Math.floor(s / 60) + ':' + (s % 60).toString().padStart(2, '0');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSendOtp = async () => {
    if (!formData.email) {
      setFeedback({ type: 'error', message: 'Please enter your email.' });
      return;
    }
    setIsSendingOtp(true);
    setFeedback({ type: '', message: '' });
    try {
      await sendOtp({ email: formData.email });
      setShowOtpVerification(true);
      setOtpTimer(300);
      setCanResendOtp(false);
      setFeedback({ type: 'success', message: 'OTP sent! Check your inbox or spam.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Failed to send OTP.' });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setFeedback({ type: 'error', message: 'Please enter the OTP.' });
      return;
    }
    if (otp.length !== 6) {
      setFeedback({ type: 'error', message: 'Enter a valid 6-digit OTP.' });
      return;
    }
    setIsVerifyingOtp(true);
    setFeedback({ type: '', message: '' });
    try {
      await verifyOtp({ email: formData.email, otp });
      try {
        await checkUserExists(formData.email);
        setIsOtpVerified(true);
        setFeedback({ type: 'success', message: 'Email verified!' });
        setOtpTimer(0);
        setCanResendOtp(false);
      } catch (userExistsError) {
        setFeedback({ type: 'error', message: 'Account already exists. Try logging in.' });
        setShowOtpVerification(false);
        setIsOtpVerified(false);
        setOtp('');
        setOtpTimer(0);
        setCanResendOtp(false);
      }
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Invalid OTP.' });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleActualSignup = async () => {
    try {
      const response = await signupCandidate({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone_country_code: formData.phoneCountryCode,
        phone_number: formData.phoneNumber,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        accept_terms: formData.acceptTerms,
      });
      SessionManager.setSession('candidate', response);
      setFeedback({ type: 'success', message: 'Account created! Redirecting...' });
      setTimeout(() => navigate('/onboarding'), 1500);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Signup failed.' });
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });
    if (formData.password !== formData.confirmPassword) {
      setFeedback({ type: 'error', message: 'Passwords do not match.' });
      return;
    }
    if (!formData.acceptTerms) {
      setFeedback({ type: 'error', message: 'Please accept the terms.' });
      return;
    }
    if (!isOtpVerified) {
      setFeedback({ type: 'error', message: 'Please verify your email first.' });
      return;
    }
    await handleActualSignup();
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="min-h-screen flex">
        {/* LEFT PANEL */}
        <div className="hidden lg:flex lg:w-[48%] relative bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-cyan-500/25 via-blue-500/20 to-transparent blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-[450px] h-[450px] rounded-full bg-gradient-to-tl from-indigo-500/25 via-purple-500/15 to-transparent blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] rounded-full bg-blue-400/10 blur-2xl"></div>
          </div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNCkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-60 pointer-events-none"></div>
          <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
            <div>
              <button onClick={() => navigate('/')} className="flex items-center space-x-2 group">
                <div className="w-10 h-10 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-colors">
                  <Icon name="smart_toy" size={22} className="text-cyan-400" />
                </div>
                <span className="text-xl font-bold text-white">Aptirix</span>
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center space-y-8 py-12">
              <div>
                <div className="inline-block px-3 py-1.5 bg-cyan-400/15 rounded-full text-xs font-semibold text-cyan-300 border border-cyan-400/25 mb-6 tracking-wide">JOIN 50,000+ PROFESSIONALS</div>
                <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">Start your career<br />journey with <span className="text-cyan-400">AI</span></h1>
                <p className="text-gray-400 text-base max-w-sm leading-relaxed">Create your free account and unlock AI-powered interview training, resume building, and career analytics.</p>
              </div>
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/30 max-w-sm">
                <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop&q=80" alt="Team collaborating" className="w-full h-52 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-cyan-500/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-cyan-400/30">
                      <Icon name="school" size={16} className="text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">AI-Powered Learning</p>
                      <p className="text-gray-400 text-xs">Personalized career coaching</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2"><Icon name="bolt" size={20} className="text-cyan-400/70" /><span className="text-gray-400 text-sm">Free to start</span></div>
                <div className="flex items-center space-x-2"><Icon name="shield" size={20} className="text-cyan-400/70" /><span className="text-gray-400 text-sm">Secure</span></div>
                <div className="flex items-center space-x-2"><Icon name="speed" size={20} className="text-cyan-400/70" /><span className="text-gray-400 text-sm">2 min setup</span></div>
              </div>
            </div>
            <p className="text-gray-500 text-xs">&copy; 2026 Aptirix. All rights reserved.</p>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="flex-1 flex items-center justify-center bg-white px-6 sm:px-12 py-8 overflow-y-auto">
          <div className="w-full max-w-[420px]">
            <div className="lg:hidden flex items-center justify-center mb-6">
              <button onClick={() => navigate('/')} className="flex items-center space-x-2">
                <div className="w-9 h-9 bg-[#0f172a] rounded-xl flex items-center justify-center">
                  <Icon name="smart_toy" size={20} className="text-cyan-400" />
                </div>
                <span className="text-xl font-bold text-gray-900">Aptirix</span>
              </button>
            </div>
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Create account</h2>
              <p className="text-gray-500 text-sm">Fill in your details to get started</p>
            </div>
            <div className="space-y-3 mb-5">
              <button type="button" onClick={handleGoogleSuccess} className="w-full h-11 bg-white border border-gray-300 rounded-lg flex items-center justify-center space-x-2.5 text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-400 transition-all duration-150">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Sign up with Google</span>
              </button>
              <button type="button" onClick={() => initiateLinkedInAuth('candidate')} className="w-full h-11 bg-white border border-gray-300 rounded-lg flex items-center justify-center space-x-2.5 text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-400 transition-all duration-150">
                <svg className="w-5 h-5" fill="#0A66C2" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/></svg>
                <span>Sign up with LinkedIn</span>
              </button>
            </div>
            <div className="flex items-center mb-5">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="px-4 text-xs text-gray-400 uppercase tracking-wider font-medium">or</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">First name</label>
                  <input type="text" value={formData.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} className="w-full h-11 px-3.5 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none text-sm transition-all" placeholder="John" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Last name</label>
                  <input type="text" value={formData.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} className="w-full h-11 px-3.5 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none text-sm transition-all" placeholder="Doe" required />
                </div>
              </div>

              {/* Email + OTP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email address {isOtpVerified && <span className="text-emerald-600 text-xs ml-2 font-normal">&#10003; Verified</span>}
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"><Icon name="mail" size={18} /></div>
                    <input type="email" value={formData.email} onChange={(e) => { handleInputChange('email', e.target.value); if (showOtpVerification) { setShowOtpVerification(false); setIsOtpVerified(false); setOtp(''); setOtpTimer(0); setCanResendOtp(false); } }} className={`w-full h-11 pl-10 pr-3.5 rounded-lg border bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none text-sm transition-all ${isOtpVerified ? 'border-emerald-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20' : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'}`} placeholder="name@company.com" required />
                  </div>
                  {!showOtpVerification && formData.email && !isOtpVerified && (
                    <button type="button" onClick={handleSendOtp} disabled={isSendingOtp} className="px-4 h-11 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors whitespace-nowrap">{isSendingOtp ? 'Sending...' : 'Verify'}</button>
                  )}
                  {showOtpVerification && !isOtpVerified && (
                    <button type="button" onClick={() => { setShowOtpVerification(false); setOtp(''); setOtpTimer(0); setCanResendOtp(false); setFeedback({ type: '', message: '' }); }} className="px-3 h-11 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">Change</button>
                  )}
                  {isOtpVerified && (
                    <div className="flex items-center px-3 h-11 bg-emerald-50 border border-emerald-200 rounded-lg"><Icon name="check_circle" size={18} className="text-emerald-600" /></div>
                  )}
                </div>
                {showOtpVerification && !isOtpVerified && (
                  <div className="mt-2.5 p-3.5 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-blue-700">Enter 6-digit verification code</span>
                      {otpTimer > 0 && <span className="text-xs text-blue-500 font-mono">{formatTimer(otpTimer)}</span>}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" value={otp} onChange={(e) => setOtp(e.target.value)} className="flex-1 h-10 rounded-lg border border-blue-300 bg-white text-gray-900 text-center tracking-widest font-mono focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none text-sm" placeholder="000000" maxLength={6} />
                      {otp.length === 6 ? (
                        <button type="button" onClick={handleVerifyOtp} disabled={isVerifyingOtp} className="px-4 h-10 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">{isVerifyingOtp ? '...' : 'Verify'}</button>
                      ) : (
                        <button type="button" onClick={handleSendOtp} disabled={isSendingOtp || !canResendOtp} className={`px-3 h-10 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${canResendOtp ? 'text-blue-600 hover:bg-blue-100' : 'text-gray-400'}`}>{canResendOtp ? 'Resend' : formatTimer(otpTimer)}</button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone number</label>
                <div className="flex gap-2">
                  <select value={formData.phoneCountryCode} onChange={(e) => handleInputChange('phoneCountryCode', e.target.value)} className="w-[72px] h-11 rounded-lg border border-gray-300 bg-white text-gray-900 text-sm px-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none">
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+91">+91</option>
                    <option value="+86">+86</option>
                    <option value="+49">+49</option>
                    <option value="+33">+33</option>
                    <option value="+81">+81</option>
                    <option value="+82">+82</option>
                  </select>
                  <div className="relative flex-1">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"><Icon name="phone" size={18} /></div>
                    <input type="tel" value={formData.phoneNumber} onChange={(e) => handleInputChange('phoneNumber', e.target.value)} className="w-full h-11 pl-10 pr-3.5 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none text-sm transition-all" placeholder="Phone number" required />
                  </div>
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"><Icon name="lock" size={18} /></div>
                  <input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => handleInputChange('password', e.target.value)} className="w-full h-11 pl-10 pr-11 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none text-sm transition-all" placeholder="Create a password" required />
                  <button type="button" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" onClick={() => setShowPassword(!showPassword)}><Icon name={showPassword ? 'visibility_off' : 'visibility'} size={18} /></button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm password</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"><Icon name="lock" size={18} /></div>
                  <input type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={(e) => handleInputChange('confirmPassword', e.target.value)} className="w-full h-11 pl-10 pr-11 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none text-sm transition-all" placeholder="Confirm your password" required />
                  <button type="button" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" onClick={() => setShowConfirmPassword(!showConfirmPassword)}><Icon name={showConfirmPassword ? 'visibility_off' : 'visibility'} size={18} /></button>
                </div>
              </div>

              {/* Terms */}
              <div className="flex items-start gap-2.5">
                <input type="checkbox" checked={formData.acceptTerms} onChange={(e) => handleInputChange('acceptTerms', e.target.checked)} className="w-4 h-4 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <label className="text-xs text-gray-500 leading-relaxed">
                  I agree to the{' '}
                  <button type="button" className="text-blue-600 hover:text-blue-700 font-medium underline">Terms of Service</button>
                  {' '}and{' '}
                  <button type="button" className="text-blue-600 hover:text-blue-700 font-medium underline">Privacy Policy</button>
                </label>
              </div>

              {/* Feedback */}
              {feedback.message && (
                <div className={`text-sm px-4 py-3 rounded-lg flex items-start space-x-2 ${feedback.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                  <Icon name={feedback.type === 'error' ? 'error' : 'check_circle'} size={18} className="mt-0.5 flex-shrink-0" />
                  <span>{feedback.message}</span>
                </div>
              )}

              {/* Submit */}
              <button type="submit" disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber || !formData.password || !formData.confirmPassword || !formData.acceptTerms || !isOtpVerified} className="w-full h-11 bg-[#0f172a] text-white text-sm font-semibold rounded-lg hover:bg-[#1e293b] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150">
                {isOtpVerified ? 'Create account' : 'Sign up'}
              </button>
            </form>

            <p className="text-center text-gray-500 text-sm mt-6">
              Already have an account?{' '}
              <button onClick={() => navigate('/login')} className="text-blue-600 hover:text-blue-700 font-semibold transition-colors">Sign in</button>
            </p>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default SignupPage;
